import { useCallback, useContext, useEffect, useMemo, useState } from "react";
import { MdBarChart } from "react-icons/md";
import { GiSoccerBall, GiBarbecue } from "react-icons/gi";
import { FaRegCalendarXmark, FaPeopleGroup } from "react-icons/fa6";
import { TrendingDown } from "lucide-react";

import Tooltip from "@mui/material/Tooltip";
import Widget from "components/widget/Widget";
import Jugadores from "views/dashboard/components/Jugadores";
import UltimoJueves, {
  EventoDelJueves,
} from "views/dashboard/components/UltimoJueves";
import AsistenciaList from "components/AsistenciaList";
import AsistenciaABMDashboard, {
  AccionEventos,
} from "components/AsistenciaABM_Dashboard";
import axiosInstance from "../../utils/axiosInstance";
import { format } from "date-fns";
import { useYear } from "context/YearContext";
import { AuthContext } from "context/AuthContext";
import { buscarEventoDelJueves, getLastThursday } from "utils/jueves";
import { compiteEnLaTabla, porcentajeDeDescenso } from "utils/zonas";

const formatDate = (isoDate: string) => {
  return format(new Date(isoDate), "dd/MM/yy");
};

interface Resumen {
  id: number;
  nombre: string;
  asistencias: number;
  partialTotal: number;
  porcentaje: string;
  deletedAt?: string | null;
}

interface Evento {
  id: number;
  fecha: string;
  description: string;
  tipo: string;
  cancelado: boolean;
  Asistencia: {
    PersonaId: number;
    presente: boolean;
    Persona?: { id: number; nombre: string };
  }[];
}

const anioDe = (evento: Evento) => new Date(evento.fecha).getFullYear();
const presentesDe = (evento: Evento) =>
  (evento.Asistencia || []).filter((a) => a.presente);

const Dashboard = () => {
  const { year: selectedYear } = useYear();
  const { isLoggedIn } = useContext(AuthContext);

  /*
   * Antes había cuatro efectos que pedían /eventos por separado (cancelados,
   * fútbol, peñas y mayor asistencia): cuatro respuestas idénticas por carga.
   * Ahora se pide una vez y todo lo demás se deriva en memoria, que además es
   * lo que hace posible comparar contra el año anterior sin pedir nada nuevo.
   */
  const [eventos, setEventos] = useState<Evento[]>([]);
  const [totalJugadores, setTotalJugadores] = useState(0);
  const [lowestPlayerNames, setLowestPlayerNames] = useState<string[]>([]);
  const [cargandoEventos, setCargandoEventos] = useState(true);

  // 1) A key that, when changed, triggers AsistenciaList to refetch
  const [refreshKey, setRefreshKey] = useState(0);

  // 2) This function is called from AsistenciaABMDashboard
  //    after "Guardar Asistencias" to update AsistenciaList
  const handleAsistenciaSaved = () => {
    setRefreshKey((prev) => prev + 1);
  };

  // Orden que el panel le pide a la tarjeta de eventos (crear o abrir uno).
  const [accionEventos, setAccionEventos] = useState<AccionEventos | null>(null);

  useEffect(() => {
    let vigente = true;

    const cargar = async () => {
      try {
        setCargandoEventos(true);
        const [eventosRes, personasRes] = await Promise.all([
          axiosInstance.get("/eventos"),
          axiosInstance.get("/personas"),
        ]);
        if (!vigente) return;
        setEventos(eventosRes.data as Evento[]);
        setTotalJugadores((personasRes.data as unknown[]).length);
      } catch (error) {
        console.error("Error al obtener los datos del panel:", error);
      } finally {
        if (vigente) setCargandoEventos(false);
      }
    };

    cargar();
    return () => {
      vigente = false;
    };
  }, [refreshKey]);

  useEffect(() => {
    let vigente = true;

    const fetchLowestAsistenciaPlayer = async () => {
      try {
        const response = await axiosInstance.get(
          `/eventos/resumen?year=${selectedYear}`
        );
        if (!vigente) return;
        const data: Resumen[] = response.data;

        /*
         * El último puesto se mide por porcentaje, igual que la tabla.
         *
         * Antes se tomaba el mínimo de asistencias absolutas, y como el total
         * de cada jugador se cuenta desde su fecha de alta, el recién llegado
         * (1 de 1, o sea 100%) era siempre el candidato: aparecía acá en
         * descenso mientras la tabla lo mostraba primero.
         */
        const datos = (p: Resumen) => ({
          porcentaje: Number(p.porcentaje) || 0,
          totalEventos: p.partialTotal,
          dadoDeBaja: Boolean(p.deletedAt),
        });

        const minimo = porcentajeDeDescenso(data, datos);
        const ultimos =
          minimo === null
            ? []
            : data.filter(
                (p) =>
                  compiteEnLaTabla(datos(p)) &&
                  (Number(p.porcentaje) || 0) === minimo
              );

        setLowestPlayerNames(ultimos.map((p) => p.nombre));
      } catch (error) {
        console.error("Error fetching lowest asistencia player:", error);
      }
    };

    fetchLowestAsistenciaPlayer();
    return () => {
      vigente = false;
    };
  }, [selectedYear, refreshKey]);

  /* ============ MÉTRICAS DERIVADAS ============ */
  const metricas = useMemo(() => {
    const anioPrevio = selectedYear - 1;

    const cuenta = (anio: number, filtro: (e: Evento) => boolean) =>
      eventos.filter((e) => anioDe(e) === anio && filtro(e)).length;

    const futbol = (e: Evento) => e.tipo === "Fútbol" && !e.cancelado;
    const penas = (e: Evento) => e.tipo === "Peña" && !e.cancelado;
    const cancelados = (e: Evento) => e.cancelado;

    // El evento con más asistentes del año, cancelados incluidos (igual que antes)
    const delAnio = eventos.filter((e) => anioDe(e) === selectedYear);
    const mayorAsistencia = delAnio.reduce<Evento | null>(
      (max, actual) =>
        presentesDe(actual).length > (max ? presentesDe(max).length : 0)
          ? actual
          : max,
      null
    );

    return {
      // Sólo tiene sentido comparar si el año anterior existe en los datos
      hayAnioPrevio: eventos.some((e) => anioDe(e) === anioPrevio),
      anioPrevio,
      futbolDelAnio: cuenta(selectedYear, futbol),
      futbolPrevio: cuenta(anioPrevio, futbol),
      penasDelAnio: cuenta(selectedYear, penas),
      penasPrevio: cuenta(anioPrevio, penas),
      canceladosDelAnio: cuenta(selectedYear, cancelados),
      canceladosPrevio: cuenta(anioPrevio, cancelados),
      futbolHistorico: eventos.filter(futbol).length,
      mayorAsistencia,
    };
  }, [eventos, selectedYear]);

  /* ============ EL ÚLTIMO JUEVES ============ */
  const ultimoJueves = useMemo(() => getLastThursday(), []);

  const eventoDelJueves: EventoDelJueves | null = useMemo(() => {
    const evento = buscarEventoDelJueves(eventos, ultimoJueves);
    if (!evento) return null;

    const presentes = presentesDe(evento);
    return {
      id: evento.id,
      tipo: evento.tipo,
      cancelado: evento.cancelado,
      cantidad: presentes.length,
      asistentes: presentes
        .map((a) => a.Persona?.nombre)
        .filter((nombre): nombre is string => Boolean(nombre)),
    };
  }, [eventos, ultimoJueves]);

  const handleCargarJueves = useCallback(() => {
    setAccionEventos({
      nonce: Date.now(),
      tipo: eventoDelJueves ? "expandir" : "crear",
      eventoId: eventoDelJueves?.id,
    });
  }, [eventoDelJueves]);

  /** Comparación contra el año anterior, sólo si ese año tiene datos. */
  const compararCon = (delta: number, mejorCuando: "sube" | "baja" = "sube") =>
    metricas.hayAnioPrevio
      ? { delta, etiqueta: String(metricas.anioPrevio), mejorCuando }
      : null;

  return (
    <div className="space-y-4">
      {/* Estado del último jueves: lo primero, porque es la pregunta con la
          que se abre la app */}
      <UltimoJueves
        fecha={ultimoJueves}
        evento={eventoDelJueves}
        totalJugadores={totalJugadores}
        puedeEditar={isLoggedIn}
        onCargar={handleCargarJueves}
        cargando={cargandoEventos}
      />

      {/* Fila de métricas: 2 / 3 / 6 columnas según el ancho disponible */}
      <div className="grid select-none grid-cols-2 gap-3 sm:gap-4 lg:grid-cols-3 3xl:grid-cols-6">
        <Widget
          icon={<MdBarChart className="h-5 w-5" />}
          title={"Partidos Totales"}
          subtitle={
            metricas.futbolHistorico
              ? (374 + metricas.futbolHistorico).toString()
              : "-"
          }
        />
        <Widget
          icon={<GiSoccerBall className="h-5 w-5" />}
          title={`Partidos ${selectedYear}`}
          subtitle={
            metricas.futbolDelAnio ? metricas.futbolDelAnio.toString() : "-"
          }
          comparacion={compararCon(
            metricas.futbolDelAnio - metricas.futbolPrevio
          )}
        />
        <Widget
          icon={<GiBarbecue className="h-5 w-5" />}
          title={`Peñas ${selectedYear}`}
          subtitle={
            metricas.penasDelAnio ? metricas.penasDelAnio.toString() : "-"
          }
          comparacion={compararCon(metricas.penasDelAnio - metricas.penasPrevio)}
        />
        <Widget
          icon={<FaPeopleGroup className="h-5 w-5" />}
          title={"Mayor Asistencia"}
          subtitle={
            metricas.mayorAsistencia
              ? formatDate(metricas.mayorAsistencia.fecha)
              : "-"
          }
        />
        <Widget
          icon={<TrendingDown className="h-5 w-5" />}
          title={"Descenso"}
          subtitle={(() => {
            const hasAnyEvents =
              metricas.futbolDelAnio + metricas.penasDelAnio > 3;
            // Si no hay eventos en el año, forzamos "-" en mobile y desktop
            if (!hasAnyEvents) {
              return <span>-</span>;
            }

            return (
              /* Texto, no cifra: baja de tamaño para no desbordar la tarjeta */
              <span className="block text-[14px] font-semibold leading-snug sm:text-[15px]">
                {/* Mobile view */}
                <span className="block sm:hidden">
                  {lowestPlayerNames.length > 0
                    ? lowestPlayerNames.join(", ")
                    : "-"}
                </span>

                {/* Desktop view */}
                <span className="hidden sm:block">
                  {lowestPlayerNames.length === 0 ? (
                    "-"
                  ) : lowestPlayerNames.length > 2 ? (
                    <Tooltip
                      title={lowestPlayerNames.join(", ")}
                      arrow
                      componentsProps={{
                        tooltip: {
                          className:
                            "!bg-white dark:!bg-navy-700 !text-navy-700 dark:!text-white !text-[13px] !shadow-pop !rounded-control !px-3 !py-2 !border !border-gray-200/80 dark:!border-white/10",
                        },
                        arrow: {
                          className: "!text-white dark:!text-navy-700",
                        },
                      }}
                    >
                      <span className="cursor-help border-b border-dashed border-gray-400">
                        {lowestPlayerNames.length} jugadores
                      </span>
                    </Tooltip>
                  ) : (
                    lowestPlayerNames.join(", ")
                  )}
                </span>
              </span>
            );
          })()}
        />

        <Widget
          icon={<FaRegCalendarXmark className="h-5 w-5" />}
          title={"Partidos Cancelados"}
          subtitle={
            metricas.canceladosDelAnio
              ? metricas.canceladosDelAnio.toString()
              : "-"
          }
          comparacion={compararCon(
            metricas.canceladosDelAnio - metricas.canceladosPrevio,
            "baja"
          )}
        />
      </div>

      {/*
        Dos columnas recién en xl: por debajo de ese ancho las dos tarjetas
        quedaban demasiado angostas para las filas de asistencias.
      */}
      <div className="grid grid-cols-1 gap-4 xl:grid-cols-2">
        {/* Pass refreshKey so AsistenciaList can re-fetch on changes */}
        <AsistenciaList refreshKey={refreshKey} />

        {/* Pass callback so AsistenciaABM_Dashboard calls it after "Guardar Asistencias" */}
        <AsistenciaABMDashboard
          onAsistenciaSaved={handleAsistenciaSaved}
          accion={accionEventos}
        />
      </div>

      {/*
        Antes esta tabla vivía en una grilla de 2 columnas con un solo hijo, así
        que ocupaba la mitad del ancho y dejaba medio panel vacío en escritorio.
      */}
      <Jugadores />
    </div>
  );
};

export default Dashboard;
